import { Framework } from '@/framework';
import { REFERENCE_LINE_DIRECTION } from './constants';
import { AddReferenceLine } from './operations/add-reference-line';
import { ClearReferenceLines } from './operations/clear-reference-lines';
import { DataOperation } from './operations/data-operation';
import { DeleteReferenceLine } from './operations/delete-reference-line';
import { LockReferenceLines } from './operations/lock-reference-lines';
import { ShowReferenceLines } from './operations/show-reference-lines';
import { UpdateReferenceLine } from './operations/update-reference-line';
import { getReferenceLinesState } from './states/reference-lines-state';
import type { ReferenceLine } from './types';

/** 用户参考线的实例级管理入口。 */
export class ReferenceLines extends Framework {
    /** 新增参考线操作。 */
    private $addReferenceLine: AddReferenceLine;
    /** 显示状态操作。 */
    private $showReferenceLines: ShowReferenceLines;
    /** 锁定状态操作。 */
    private $lockReferenceLines: LockReferenceLines;
    /** 删除参考线操作。 */
    private $deleteReferenceLine: DeleteReferenceLine;
    /** 更新参考线操作。 */
    private $updateReferenceLine: UpdateReferenceLine;
    /** 清空参考线操作。 */
    private $clearReferenceLines: ClearReferenceLines;
    /** 参考线数据导入导出操作。 */
    private $dataOperation: DataOperation;

    constructor(id: string) {
        super(id);
        this.$addReferenceLine = new AddReferenceLine(id);
        this.$showReferenceLines = new ShowReferenceLines(id);
        this.$lockReferenceLines = new LockReferenceLines(id);
        this.$deleteReferenceLine = new DeleteReferenceLine(id);
        this.$updateReferenceLine = new UpdateReferenceLine(id);
        this.$clearReferenceLines = new ClearReferenceLines(id);
        this.$dataOperation = new DataOperation(id);
    }

    /** 添加水平参考线 */
    addHorizontalReferenceLine(pushHistory = true) {
        return this.$addReferenceLine.add(REFERENCE_LINE_DIRECTION.HORIZONTAL, pushHistory);
    }

    /** 添加垂直参考线 */
    addVerticalReferenceLine(pushHistory = true) {
        return this.$addReferenceLine.add(REFERENCE_LINE_DIRECTION.VERTICAL, pushHistory);
    }

    /** 按给定数据新增参考线，默认写入历史。 */
    addReferenceLine(line: Partial<ReferenceLine>, pushHistory = true) {
        return this.$addReferenceLine.addWithLineData(line, pushHistory);
    }

    /** 显示参考线 */
    showReferenceLines() {
        this.$showReferenceLines.show(true);
    }

    /** 隐藏参考线 */
    hideReferenceLines() {
        this.$showReferenceLines.show(false);
    }

    /** 锁定参考线 */
    lockReferenceLines() {
        this.$lockReferenceLines.lock(true);
    }

    /** 解锁参考线 */
    unlockReferenceLines() {
        this.$lockReferenceLines.lock(false);
    }

    /** 删除参考线 */
    deleteReferenceLine(id: string, pushHistory = true) {
        this.$deleteReferenceLine.delete(id, pushHistory);
    }

    /** 更新参考线 */
    updateReferenceLine(line: ReferenceLine, oldLine?: ReferenceLine, pushHistory = true) {
        this.$updateReferenceLine.update(line, oldLine, pushHistory);
    }

    /** 清除参考线 */
    clearReferenceLines() {
        this.$clearReferenceLines.clear();
    }

    /** 获取参考线数据 */
    getReferenceLineData() {
        return this.$dataOperation.get();
    }

    /** 恢复参考线数据 */
    restoreReferenceLineData(data: { lines: ReferenceLine[]; show?: boolean; lock?: boolean }) {
        this.$dataOperation.set(data);
    }

    /** 设置参考线 */
    setReferenceLines(lines: ReferenceLine[]) {
        getReferenceLinesState(this.identity).setReferenceLines(lines);
    }
}
